import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};

// Helper to get/set guest cart from localStorage
const GUEST_CART_KEY = 'depro_guest_cart';

const getGuestCartFromStorage = () => {
  try {
    const stored = localStorage.getItem(GUEST_CART_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Error reading guest cart:', e);
  }
  return { items: [], subtotal: 0, shipping: 0, total: 0 };
};

const saveGuestCartToStorage = (cart) => {
  try {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart));
  } catch (e) {
    console.error('Error saving guest cart:', e);
  }
};

export const CartProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [cart, setCart] = useState({ items: [], subtotal: 0, shipping: 0, total: 0 });
  const [guestCart, setGuestCart] = useState(() => getGuestCartFromStorage());
  const [loading, setLoading] = useState(false);

  // Save guest cart to localStorage when it changes (but not on initial empty state)
  useEffect(() => {
    // Only save if there are items or if we're clearing an existing cart
    if (guestCart.items.length > 0) {
      saveGuestCartToStorage(guestCart);
    }
  }, [guestCart]);

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated || !token) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(res.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Calculate guest cart totals
  const calculateGuestCartTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    return {
      items,
      subtotal,
      shipping: 0,
      total: subtotal
    };
  };

  const addToCart = async (productId, size, quantity = 1) => {
    if (isAuthenticated) {
      // Logged in user - use server cart
      try {
        await axios.post(`${API}/cart/add`, 
          { product_id: productId, size, quantity },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        await fetchCart();
        toast.success('Adăugat în coș');
        return true;
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Eroare la adăugare în coș');
        return false;
      }
    } else {
      // Guest user - use local cart
      try {
        // Fetch product details by ID
        const productRes = await axios.get(`${API}/products/${productId}`);
        const product = productRes.data;
        
        if (!product) {
          toast.error('Produsul nu a fost găsit');
          return false;
        }
        
        const existingIndex = guestCart.items.findIndex(
          item => item.product_id === productId && item.size === size
        );

        let newItems;
        if (existingIndex >= 0) {
          newItems = [...guestCart.items];
          newItems[existingIndex].quantity += quantity;
        } else {
          newItems = [...guestCart.items, {
            product_id: productId,
            product_name: product.name,
            product_image: product.images[0] || '',
            name: product.name,
            image: product.images[0] || '',
            size,
            quantity,
            price: product.price
          }];
        }

        setGuestCart(calculateGuestCartTotals(newItems));
        toast.success('Adăugat în coș');
        return true;
      } catch (error) {
        console.error('Guest cart error:', error);
        toast.error('Eroare la adăugare în coș');
        return false;
      }
    }
  };

  const updateQuantity = async (productId, size, quantity) => {
    if (isAuthenticated) {
      try {
        await axios.put(`${API}/cart/update`,
          { product_id: productId, size, quantity },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        await fetchCart();
      } catch (error) {
        toast.error('Eroare la actualizare coș');
      }
    } else {
      // Guest cart update
      if (quantity <= 0) {
        removeFromCart(productId, size);
        return;
      }
      const newItems = guestCart.items.map(item => 
        item.product_id === productId && item.size === size
          ? { ...item, quantity }
          : item
      );
      setGuestCart(calculateGuestCartTotals(newItems));
    }
  };

  const removeFromCart = async (productId, size) => {
    if (isAuthenticated) {
      try {
        await axios.delete(`${API}/cart/${productId}/${size}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        await fetchCart();
        toast.success('Eliminat din coș');
      } catch (error) {
        toast.error('Eroare la eliminare');
      }
    } else {
      // Guest cart remove
      const newItems = guestCart.items.filter(
        item => !(item.product_id === productId && item.size === size)
      );
      setGuestCart(calculateGuestCartTotals(newItems));
      toast.success('Eliminat din coș');
    }
  };

  const clearCart = async () => {
    if (isAuthenticated) {
      try {
        await axios.delete(`${API}/cart`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCart({ items: [], subtotal: 0, shipping: 0, total: 0 });
      } catch (error) {
        console.error('Failed to clear cart');
      }
    }
  };

  const clearGuestCart = () => {
    setGuestCart({ items: [], subtotal: 0, shipping: 0, total: 0 });
    localStorage.removeItem(GUEST_CART_KEY);
  };

  // Get the active cart based on auth status
  const activeCart = isAuthenticated ? cart : guestCart;
  const itemCount = activeCart.items.reduce((sum, item) => sum + item.quantity, 0);

  const value = {
    cart,
    guestCart,
    loading,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    clearGuestCart,
    fetchCart,
    itemCount,
    activeCart
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
